import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingDownIcon, TrendingUpIcon, MinusIcon } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
function AnimatedCounter({
  target,
  suffix = '',
  prefix = '',
  duration = 1.5
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target * 10) / 10);
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target, duration]);
  return <span ref={ref} data-id="element-91">
      {prefix}
      {typeof target === 'number' && target % 1 !== 0 ? count.toFixed(1) : Math.round(count)}
      {suffix}
    </span>;
}
function ProgressRing({
  value,
  color,
  size = 80
}: {
  value: number;
  color: string;
  size?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const [offset, setOffset] = useState(circumference);
  useEffect(() => {
    if (isInView) {
      setOffset(circumference - value / 100 * circumference);
    }
  }, [isInView, value, circumference]);
  return <div ref={ref} className="relative" style={{
    width: size,
    height: size
  }} data-id="element-92">
      <svg width={size} height={size} className="-rotate-90" data-id="element-93">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#1E2D45" strokeWidth={6} data-id="element-94" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={6} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{
        transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
      }} data-id="element-95" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-96">
        <span className="text-white font-black text-sm" data-id="element-97">{value}%</span>
      </div>
    </div>;
}
const sparklineData = [[{
  v: 45
}, {
  v: 52
}, {
  v: 48
}, {
  v: 61
}, {
  v: 58
}, {
  v: 67
}, {
  v: 67
}], [{
  v: 38
}, {
  v: 41
}, {
  v: 39
}, {
  v: 44
}, {
  v: 42
}, {
  v: 43
}, {
  v: 43
}], [{
  v: 18
}, {
  v: 16
}, {
  v: 15
}, {
  v: 14
}, {
  v: 13
}, {
  v: 12
}, {
  v: 12
}], [{
  v: -0.5
}, {
  v: -0.8
}, {
  v: -1.2
}, {
  v: -1.8
}, {
  v: -2.0
}, {
  v: -2.3
}, {
  v: -2.3
}]];
const adoptionData = [{
  team: 'Frontend',
  value: 78
}, {
  team: 'Backend',
  value: 62
}, {
  team: 'Platform',
  value: 45
}, {
  team: 'Mobile',
  value: 55
}, {
  team: 'Data',
  value: 38
}];
const metrics = [{
  label: '% AI-Assisted Commits',
  sublabel: 'per developer',
  value: 67,
  display: '67%',
  trend: 'up',
  trendValue: '+8% MoM',
  color: '#00D4FF',
  type: 'ring',
  sparkIndex: 0
}, {
  label: 'AI Suggestion',
  sublabel: 'Acceptance Rate',
  value: 43,
  display: '43%',
  trend: 'up',
  trendValue: '+3% MoM',
  color: '#3B82F6',
  type: 'ring',
  sparkIndex: 1
}, {
  label: 'AI Rework Rate',
  sublabel: 'lower is better',
  value: 12,
  display: '12%',
  trend: 'down',
  trendValue: '-4% MoM',
  color: '#10B981',
  type: 'ring',
  sparkIndex: 2
}, {
  label: 'AI-Assisted',
  sublabel: 'Cycle Time Delta',
  value: null,
  display: '-2.3d',
  trend: 'down-good',
  trendValue: 'Faster delivery',
  color: '#10B981',
  type: 'number',
  sparkIndex: 3
}];
export function AIUsageSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative" data-id="element-98">
      <div className="absolute inset-0 dot-bg opacity-40" data-id="element-99" />
      <div className="max-w-6xl mx-auto relative" data-id="element-100">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-4" data-id="element-101">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-102">
            Human + AI Collaboration Intelligence
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="mb-12" data-id="element-103">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3" data-id="element-104">
            Adoption Does Not Equal Impact
          </h2>
          <p className="text-xl text-cyan-400 font-medium" data-id="element-105">
            Waydev shows the difference.
          </p>
        </motion.div>

        {/* Metric cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-106">
          {metrics.map((metric, i) => <motion.div key={metric.label} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: i * 0.1,
          duration: 0.5
        }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-5" data-id="element-107">
              <div className="text-xs text-gray-500 mb-1" data-id="element-108">{metric.label}</div>
              <div className="text-xs text-gray-600 mb-4" data-id="element-109">
                {metric.sublabel}
              </div>

              <div className="flex items-center justify-between mb-4" data-id="element-110">
                {metric.type === 'ring' ? <ProgressRing value={metric.value!} color={metric.color} size={72} data-id="element-111" /> : <div className="text-4xl font-black" style={{
              color: metric.color
            }} data-id="element-112">
                    <AnimatedCounter target={2.3} prefix="-" suffix="d" duration={1.2} data-id="element-113" />
                  </div>}
              </div>

              <div className="h-12" data-id="element-114">
                <ResponsiveContainer width="100%" height="100%" data-id="element-115">
                  <AreaChart data={sparklineData[metric.sparkIndex]} margin={{
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
              }} data-id="element-116">
                    <defs data-id="element-117">
                      <linearGradient id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1" data-id="element-118">
                        <stop offset="0%" stopColor={metric.color} stopOpacity={0.3} data-id="element-119" />
                        <stop offset="100%" stopColor={metric.color} stopOpacity={0} data-id="element-120" />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke={metric.color} strokeWidth={1.5} fill={`url(#grad-${i})`} dot={false} data-id="element-121" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 flex items-center gap-1.5" data-id="element-122">
                {metric.trend === 'up' && <TrendingUpIcon className="w-3 h-3 text-green-400" data-id="element-123" />}
                {metric.trend === 'down' && <TrendingDownIcon className="w-3 h-3 text-red-400" data-id="element-124" />}
                {metric.trend === 'down-good' && <TrendingDownIcon className="w-3 h-3 text-green-400" data-id="element-125" />}
                <span className={`text-xs font-medium ${metric.trend === 'down' ? 'text-red-400' : 'text-green-400'}`} data-id="element-126">
                  {metric.trendValue}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* AI Adoption Distribution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-6" data-id="element-127">
          <div className="flex items-center justify-between mb-6" data-id="element-128">
            <div data-id="element-129">
              <div className="text-white font-semibold" data-id="element-130">
                AI Adoption Distribution
              </div>
              <div className="text-gray-500 text-sm" data-id="element-131">
                Across teams — % AI-assisted commits
              </div>
            </div>
            <span className="text-xs text-gray-600 bg-[#1E2D45] px-3 py-1 rounded-full" data-id="element-132">
              Last 30 days
            </span>
          </div>
          <div className="h-40" data-id="element-133">
            <ResponsiveContainer width="100%" height="100%" data-id="element-134">
              <BarChart data={adoptionData} barSize={32} data-id="element-135">
                <XAxis dataKey="team" tick={{
                fill: '#6B7280',
                fontSize: 11
              }} axisLine={false} tickLine={false} data-id="element-136" />
                <YAxis hide domain={[0, 100]} data-id="element-137" />
                <Tooltip contentStyle={{
                backgroundColor: '#0D1421',
                border: '1px solid #1E2D45',
                borderRadius: 8
              }} labelStyle={{
                color: '#fff'
              }} itemStyle={{
                color: '#00D4FF'
              }} formatter={(v: number) => [`${v}%`, 'AI Adoption']} data-id="element-138" />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-139">
                  {adoptionData.map((entry, index) => <Cell key={index} fill={entry.value >= 70 ? '#00D4FF' : entry.value >= 50 ? '#3B82F6' : '#1E2D45'} data-id="element-140" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </section>;
}